import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiLink from '../../ui/base-components/pai-link';
import PaiButton from '../../ui/base-components/pai-button';
import PaiTextArea from '../../ui/base-components/pai-text-area';
import { LinkedinAccount } from './linkedin-accounts.types';

interface Props {
  open: boolean;
  account: LinkedinAccount | null;
  signature: string;
  loading: boolean;

  handleSignatureChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClose(): void;
  handleSaveSignature(): void;
}

const EditSignatureDialog: React.FC<Props> = ({
  open,
  signature,
  loading,
  handleSignatureChange,
  handleClose,
  handleSaveSignature,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'sm'} transitionDuration={0}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('editLinkedinSignature')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={4}>
          <Stack gap={1}>
            <Typography className={classes.formTitle}>{t('linkedinSignature')}</Typography>
            <Typography className={classes.clarificationText}>{t('linkedinSignatureClarification')}</Typography>
            <PaiTextArea
              value={signature}
              placeholder={t('enterSignature')}
              rows={4}
              onChange={handleSignatureChange}
            />
            <Typography className={classes.exampleText}>
              {t('signatureExample')}: John Doe | Sales Manager | Company Name
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton
          text={t('save')}
          size={'small'}
          border={'rounded'}
          action={handleSaveSignature}
          disabled={loading}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
    padding: '20px',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  formTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  clarificationText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.primary.main,
    marginBottom: '4px',
  },
  exampleText: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    marginTop: '4px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default EditSignatureDialog;